import React, { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { supabase } from '../../../supabaseClient';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Breadcrumbs,
  Link,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

const UserIntegrationsPage = () => {
  const { userId } = useParams();
  const [companyId, setCompanyId] = useState(null);
  const [platforms, setPlatforms] = useState([{ id: 1, name: 'Trendyol' }]);
  const [selectedPlatform, setSelectedPlatform] = useState('Trendyol');
  const [shopUrl, setShopUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      try {
        // Load target user's company
        const { data: profile } = await supabase
          .from('user_profiles')
          .select('company_id')
          .eq('user_id', userId)
          .maybeSingle();
        if (profile?.company_id) {
          setCompanyId(profile.company_id);
        } else {
          setError('Target user has no company.');
        }
        // Load platforms
        try {
          const { data: plats } = await supabase
            .schema('market')
            .from('platforms')
            .select('id,name')
            .order('name');
          if (Array.isArray(plats) && plats.length > 0) {
            setPlatforms(plats);
            setSelectedPlatform(plats[0].name);
          }
        } catch (_) {}
        // Load existing integrations
        if (profile?.company_id) {
          await refreshIntegrations(profile.company_id);
        }
      } catch (e) {
        setError(e.message || 'Failed to initialize.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [userId]);

  const refreshIntegrations = async (company) => {
    try {
      const { data } = await supabase
        .schema('market')
        .from('store_integrations')
        .select('id, store_id, platform_id, is_active, stores(shop_url), platforms(name)')
        .eq('company_id', company);
      setIntegrations(data || []);
    } catch (e) {
      // best-effort
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (!companyId) throw new Error('Missing company');
      if (!shopUrl) throw new Error('Shop URL is required');
      const { data: platform } = await supabase
        .schema('market')
        .from('platforms')
        .select('id')
        .eq('name', selectedPlatform)
        .maybeSingle();
      if (!platform?.id) throw new Error('Marketplace not found');
      const { data: storeUp, error: storeErr } = await supabase
        .schema('market')
        .from('stores')
        .upsert({ company_id: companyId, shop_url: shopUrl }, { onConflict: 'company_id,shop_url' })
        .select('id')
        .maybeSingle();
      if (storeErr) throw storeErr;
      const storeId = storeUp?.id || (Array.isArray(storeUp) && storeUp[0]?.id);
      if (!storeId) throw new Error('Failed to create or fetch store');
      const { data: existing } = await supabase
        .schema('market')
        .from('store_integrations')
        .select('id')
        .eq('company_id', companyId)
        .eq('store_id', storeId)
        .eq('platform_id', platform.id)
        .maybeSingle();
      if (existing?.id) {
        const { error: updErr } = await supabase
          .schema('market')
          .from('store_integrations')
          .update({ api_key: apiKey || null, api_secret: apiSecret || null, is_active: true })
          .eq('id', existing.id);
        if (updErr) throw updErr;
      } else {
        const { error: insErr } = await supabase
          .schema('market')
          .from('store_integrations')
          .insert({ company_id: companyId, store_id: storeId, platform_id: platform.id, api_key: apiKey || null, api_secret: apiSecret || null, is_active: true });
        if (insErr) throw insErr;
      }
      setSuccess('Integration saved successfully.');
      await refreshIntegrations(companyId);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container>
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 2, mt: 2 }}>
        <Link component={RouterLink} underline="hover" color="inherit" to="/admin">Admin Panel</Link>
        <Link component={RouterLink} underline="hover" color="inherit" to={`/admin/user/${userId}`}>User Details</Link>
        <Typography color="text.primary">Integrations</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ mt: 1, mb: 2 }}>
        Marketplace Integrations (User)
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="platform-label">Marketplace</InputLabel>
              <Select labelId="platform-label" value={selectedPlatform} label="Marketplace" onChange={(e) => setSelectedPlatform(e.target.value)}>
                {platforms.map((p) => (
                  <MenuItem key={p.id} value={p.name}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8}>
            <TextField fullWidth label="Shop URL" placeholder="https://www.trendyol.com/magaza/..." value={shopUrl} onChange={(e) => setShopUrl(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="API Key (optional)" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField fullWidth label="API Secret (optional)" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Button component="a" href="/market-portal/" target="_blank" rel="noopener noreferrer" sx={{ mr: 1 }}>
              Open Marketplace Portal
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving || loading}>Save</Button>
          </Grid>
        </Grid>
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 1 }}>Existing Integrations</Typography>
      {(integrations || []).map((it) => (
        <Paper key={it.id} sx={{ p: 1, my: 1 }}>
          <Typography variant="body2">{it.platforms?.name} - {it.stores?.shop_url} {it.is_active ? '(active)' : '(inactive)'}</Typography>
        </Paper>
      ))}
    </Container>
  );
};

export default UserIntegrationsPage;


